import Dexie, { type Table } from "dexie";
import type { DisparadorTipo } from "@huayruro/shared";

// Cache local (lecturas) + cola de escrituras idempotente (§9, decisión D7).
// Sin PowerSync, sin sync bidireccional de estado: lecturas = cache, escrituras = cola.

export type ProductoLocal = {
  id: string;
  nombre: string;
  presentacion: string | null;
  laboratorio: string | null;
  principio_activo: string | null;
  categoria: string | null;
  /** Buscador (16-oct-2026): marca/generico/aseo/galenico; null = automático por el nombre. Las copias viejas no lo traen. */
  tipo?: string | null;
  /** Freno de receta (26-oct-2026): 1 = pide receta. Las copias anteriores no lo traen (se piden enteras otra vez). */
  requiere_receta?: number;
  activo: number;
  updated_at: string;
};

export type PresentacionLocal = {
  id: string;
  producto_id: string;
  nombre: string;
  factor_unidades: number;
  es_base: number;
};

export type CodigoLocal = { gtin: string; producto_id: string; presentacion_id: string | null };

export type PrecioLocal = {
  id: string; // `${producto_id}:${presentacion_id}`
  producto_id: string;
  presentacion_id: string;
  precio_sin_igv_dm: number;
  precio_total_dm: number;
  vigente_desde: string;
};

export type StockCache = {
  producto_id: string;
  stock_unidades: number;
  stock_minimo: number;
  /** Dónde está en esta botica (buscador, 16-oct-2026). Las copias viejas no lo traen. */
  ubicacion?: string | null;
  updated_at: string;
};

// A4: reglas de venta cruzada cacheadas para que el motor decida SIN red. Son de tenant y son
// poquísimas (5–10), así que se reemplazan enteras en cada sync — así una regla que el admin borró
// desaparece del mostrador sola, sin lógica de tombstones.
export type ReglaLocal = {
  id: string;
  disparador_tipo: DisparadorTipo;
  disparador_valor: string;
  sugerido_producto_id: string;
  guion: string;
  prioridad: number;
};

// Copiloto del mostrador, etapa 1 (Kevin, 16-oct-2026): preguntas y recomendaciones por principio
// activo, de la cadena. Pocas filas: se reemplazan enteras en cada sync, igual que las reglas.
export type PreguntaLocal = { id: string; texto: string; orden: number };
export type RecomendacionLocal = { id: string; principio_activo: string; texto: string };
/** Qué hace cada principio activo (buscador, 16-oct-2026). */
export type AccionLocal = { id: string; principio_activo: string; texto: string };

export type TipoOp = "venta" | "quiebre" | "recepcion" | "no_sale" | "sugerencia" | "por_crear";
export type EstadoOp = "pendiente" | "enviando" | "confirmada" | "rechazada";

// Una operación de escritura, idempotente por client_uuid (§9). El payload es el body del POST.
export type ColaOp = {
  client_uuid: string;
  tipo: TipoOp;
  payload: unknown;
  creado_at: string;
  estado: EstadoOp;
  intentos: number;
  ultimo_error: string | null;
  proximo_intento_at: string | null; // backoff
  venta_id: string | null; // asignado por el server al confirmar (para reimprimir)
  // D11 (Kevin, 30-sep): lo que se le mostró a quien cobró, para la lista de «ventas rechazadas» de este
  // equipo. Solo vive aquí: NO viaja al servidor (el enviador manda `payload`). Las ops viejas no lo traen.
  resumen?: ResumenOp | null;
  // Solo las del dueño: la botica en la que se hizo (X-Botica). Se fija al encolar, no al enviar: si el
  // dueño cambia de botica con ventas sin enviar, cada una llega a la botica donde se cobró.
  botica?: string | null;
};

export type ResumenOp = { items: { nombre: string; presentacion: string; cantidad: number }[]; total_cent: number };

export type SesionCache = {
  clave: "actual";
  token: string;
  usuario: unknown;
  sucursal: unknown;
  rol: string;
  guardado_at: string;
};

export type Meta = { clave: string; valor: unknown };

export class HuayruroDB extends Dexie {
  productos!: Table<ProductoLocal, string>;
  presentaciones!: Table<PresentacionLocal, string>;
  codigos!: Table<CodigoLocal, string>;
  precios!: Table<PrecioLocal, string>;
  stock_cache!: Table<StockCache, string>;
  cola_ops!: Table<ColaOp, string>;
  sesion_cache!: Table<SesionCache, string>;
  meta!: Table<Meta, string>;
  reglas!: Table<ReglaLocal, string>;
  copiloto_preguntas!: Table<PreguntaLocal, string>;
  copiloto_recomendaciones!: Table<RecomendacionLocal, string>;
  copiloto_acciones!: Table<AccionLocal, string>;

  constructor(nombre = "huayruro-pos") {
    super(nombre);
    this.version(1).stores({
      productos: "id, nombre, updated_at, activo",
      presentaciones: "id, producto_id",
      codigos: "gtin, producto_id",
      precios: "id, producto_id",
      stock_cache: "producto_id, updated_at",
      // La cola: PK = client_uuid (idempotencia); índices por estado y orden FIFO.
      cola_ops: "client_uuid, estado, creado_at",
      sesion_cache: "clave",
      meta: "clave",
    });
    // v2 (S15): reglas de venta cruzada. Dexie conserva las tablas de la v1 y migra en sitio, así
    // que un mostrador que ya estaba instalado NO pierde su cola pendiente al actualizar.
    this.version(2).stores({ reglas: "id" });
    // v3 (16-oct-2026): copiloto. Igual que la v2: tablas nuevas, la cola pendiente no se toca.
    this.version(3).stores({ copiloto_preguntas: "id", copiloto_recomendaciones: "id" });
    // v4 (16-oct-2026): acción por principio activo para el buscador. Igual: tabla nueva, la cola no se toca.
    this.version(4).stores({ copiloto_acciones: "id" });
  }
}

// Instancia por defecto (los tests crean la suya con nombre propio para aislarse).
export const dbLocal = new HuayruroDB();
